import type { OptionsDocument } from 'paintscout'
import type { FeatureProduct, ProductOption } from '../../options'

/**
 * The products shown in an estimate's Products section, keyed by product key: Sherwin-Williams
 * library products (`featureProduct`) and, when `includeCustomProducts` is set, custom products the
 * company opted in (`featureCustomProduct`). The customer view re-runs this on the stubs it returns
 * (see the API's filterOptionsDetails), so each stub keeps the marker that got it included.
 *
 * `includeCustomProducts` is the `customProductVisuals` LD flag. It defaults to off so a caller that
 * doesn't pass the flag never shows custom products while the flag is off for the company.
 */
export const getFeatureProducts = ({
  options,
  includeCustomProducts = false
}: {
  options: OptionsDocument
  includeCustomProducts?: boolean
}): { [key: string]: FeatureProduct } => {
  const featureProducts: { [key: string]: FeatureProduct } = {}
  Object.keys(options.options?.products?.values).forEach((key) => {
    const product = options.options.products.values[key]
    if (product.featureProduct || (includeCustomProducts && product.featureCustomProduct)) {
      featureProducts[key] = {
        key,
        label: product.label,
        description: product.description,
        featureImage: product.featureImage,
        featureProduct: !!product.featureProduct,
        ...(!product.featureProduct && { featureCustomProduct: true })
      }
    }
  })

  return featureProducts
}

/**
 * Splits feature products into the Sherwin-Williams group and the custom group, keeping each
 * group's order. The Products tables show SW first, under the SW header and logo.
 */
export const groupFeatureProducts = <T extends FeatureProduct>(
  products: T[]
): { sherwinWilliams: T[]; custom: T[] } => ({
  sherwinWilliams: products.filter((product) => product.featureProduct),
  custom: products.filter((product) => !product.featureProduct)
})

/** Feature products in display order: Sherwin-Williams first, then custom, each group in its own order. */
export const sortFeatureProducts = <T extends FeatureProduct>(products: T[]): T[] => {
  const { sherwinWilliams, custom } = groupFeatureProducts(products)
  return [...sherwinWilliams, ...custom]
}

/**
 * The `file` the Products tables pass to FilePreview for a feature product's image.
 *
 * SW images live in the legacy `paintscout` S3 bucket, which returns no Access-Control-Allow-Origin.
 * FilePreview otherwise loads file.src with crossOrigin="anonymous" (for the Konva editor's
 * tainted-canvas avoidance), which fails the CORS check and renders a blank image. These images are
 * display-only, so flagging them provider-hosted opts them out of crossOrigin.
 *
 * A custom product's image is the company's own upload (ENG-2237), so it resolves like any other
 * uploaded file through its Cloudinary id or S3 key, with FilePreview's normal CORS handling.
 *
 * Returns null when the product has no image, so the tables leave the image out rather than show
 * FilePreview's "not found" placeholder (a description with no image is allowed).
 */
export const getFeatureProductFile = (item: FeatureProduct) => {
  const image = item?.featureImage
  if (!image?.value && !image?.cloudinaryId && !image?.s3PublicKey) {
    return null
  }
  if (item?.featureProduct) {
    return { ...image, src: image?.value ?? null, provider: 'sherwin-williams', visibility: 'visible' as const }
  }
  return {
    src: image?.value ?? null,
    cloudinaryPublicId: image?.cloudinaryId,
    s3PublicKey: image?.s3PublicKey,
    format: image?.fileType,
    width: image?.width,
    height: image?.height,
    visibility: 'visible' as const
  }
}

/**
 * The image a product tile (settings list, product picker) shows for a product, or undefined for
 * none. SW products always show theirs. A custom product's image only exists through the
 * `customProductVisuals` feature, so it shows only while that flag is on.
 */
export const getProductThumbnailSrc = (
  product: Pick<ProductOption, 'featureProduct' | 'featureImage'>,
  includeCustomProducts = false
): string | undefined => {
  if (!product.featureProduct && !includeCustomProducts) {
    return undefined
  }
  return product.featureImage?.value || undefined
}
